import React from "react";
import { useTranslation } from "react-i18next";
import {
  Baby,
  Sprout,
  Search,
  GraduationCap,
  Book,
  Languages,
  Award,
  Star,
  Crown,
  Ghost,
  Zap,
  Sparkles,
  Brain,
  BookOpen,
  Eye,
  Rocket,
} from "lucide-react";
import { getRankProgress } from "@/lib/titles";

const TITLE_LUCIDE_ICONS: Record<string, React.ReactNode> = {
  Baby: <Baby size={28} strokeWidth={2} />,
  Sprout: <Sprout size={28} strokeWidth={2} />,
  Search: <Search size={28} strokeWidth={2} />,
  GraduationCap: <GraduationCap size={28} strokeWidth={2} />,
  Book: <Book size={28} strokeWidth={2} />,
  Languages: <Languages size={28} strokeWidth={2} />,
  Award: <Award size={28} strokeWidth={2} />,
  Star: <Star size={28} strokeWidth={2} />,
  Crown: <Crown size={28} strokeWidth={2} />,
  Ghost: <Ghost size={28} strokeWidth={2} />,
  Zap: <Zap size={28} strokeWidth={2} />,
  Sparkles: <Sparkles size={28} strokeWidth={2} />,
  Brain: <Brain size={28} strokeWidth={2} />,
  BookOpen: <BookOpen size={28} strokeWidth={2} />,
  Eye: <Eye size={28} strokeWidth={2} />,
  Rocket: <Rocket size={28} strokeWidth={2} />,
};

interface RankProgressWidgetProps {
  xp: number;
  compact?: boolean;
}

export default function RankProgressWidget({ xp, compact = false }: RankProgressWidgetProps) {
  const { t } = useTranslation();
  const rankProgress = getRankProgress(xp);

  return (
    <div
      style={{
        borderRadius: "var(--radius)",
        padding: compact ? 16 : 20,
        background: "linear-gradient(135deg, rgba(99,102,241,.08), rgba(168,85,247,.08))",
        border: "1px solid rgba(139,92,246,.25)",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.1em",
          color: "hsl(var(--muted-foreground))",
          marginBottom: 12,
          textTransform: "uppercase",
        }}
      >
        {t("achievements.rankProgress.label")}
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: compact ? "column" : "row",
          alignItems: compact ? "stretch" : "center",
          gap: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <div
            style={{
              width: compact ? 44 : 52,
              height: compact ? 44 : 52,
              borderRadius: 12,
              background: "linear-gradient(135deg, #6366f1, #a855f7)",
              boxShadow: "0 0 20px rgba(139,92,246,.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            {TITLE_LUCIDE_ICONS[rankProgress.current.lucideIcon]}
          </div>
          <div>
            <div
              style={{
                fontSize: compact ? 15 : 17,
                fontWeight: 700,
                color: "hsl(var(--foreground))",
              }}
            >
              {t(`achievements.shop.items.${rankProgress.current.id}`)}
            </div>
            <div
              style={{
                fontSize: 11,
                color: "hsl(var(--muted-foreground))",
                marginTop: 2,
              }}
            >
              {rankProgress.isMaxTier
                ? t("achievements.rankProgress.maxReached")
                : t("achievements.rankProgress.toNext", {
                    current: rankProgress.crystalsInSegment.toLocaleString(),
                    target: rankProgress.crystalsNeededForNext.toLocaleString(),
                    nextTitle: rankProgress.next ? t(`achievements.shop.items.${rankProgress.next.id}`) : "",
                    percent: Math.round(rankProgress.progressPercent),
                  })}
            </div>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              flex: 1,
              height: compact ? 8 : 10,
              borderRadius: 999,
              background: "hsl(var(--bg3))",
              overflow: "hidden",
              boxShadow: "inset 0 1px 2px rgba(0,0,0,.1)",
            }}
          >
            <div
              style={{
                height: "100%",
                borderRadius: 999,
                background: "linear-gradient(90deg, #6366f1, #a855f7)",
                width: `${rankProgress.progressPercent}%`,
                boxShadow: "0 0 12px rgba(139,92,246,.5)",
                transition: "width 0.5s ease-out",
              }}
            />
          </div>
          {rankProgress.next && (
            <span
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: "hsl(var(--muted-foreground))",
                flexShrink: 0,
                maxWidth: 100,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={t(`achievements.shop.items.${rankProgress.next.id}`)}
            >
              → {t(`achievements.shop.items.${rankProgress.next.id}`)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
